import { GetterTree } from 'vuex'

import { OptionGroup, OptionGroupContext } from '@/models'

import { orderByOptionGroupSequence } from '@/utils'

import { RootState } from '../../state'
import { MenuState } from '../state'

const getters: GetterTree<MenuState, RootState> = {
  /**
   * Returns an OptionGroup by its context, or null if not found.
   *
   * It throws if the OptionGroup is not loaded but its id is in an ItemGroup relation.
   *
   * @param context OptionGroupContext to be found.
   */
  optionGroupByContext: (state) => ({ id, itemGroupId }: OptionGroupContext): OptionGroup | null => {
    const itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]

    if (itemGroupOptionGroups && itemGroupOptionGroups.ids.has(id)) {
      const optionGroup = state.optionGroups.elements.find((i) => i.id === id)

      if (optionGroup === undefined) {
        throw new Error(`OptionGroup "${id}" was not found, but it is in the ItemGroup "${itemGroupId}" relation`)
      }

      return optionGroup
    }

    return null
  },

  /**
   * Returns all OptionGroups in an ItemGroup.
   *
   * It throws if the OptionGroup is not loaded but its id is in an ItemGroup relation.
   *
   * @param itemGroupId ItemGroup id to search.
   */
  optionGroupsByItemGroupId: (state) => (itemGroupId: number): OptionGroup[] => {
    const itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]

    if (itemGroupOptionGroups === undefined) {
      return []
    }

    const optionGroups: OptionGroup[] = []

    for (const id of itemGroupOptionGroups.ids) {
      const optionGroup = state.optionGroups.elements.find((i) => i.id === id)

      if (optionGroup === undefined) {
        throw new Error(`OptionGroup "${id}" was not found, but it is in the ItemGroup "${itemGroupId}" relation`)
      }

      optionGroups.push(optionGroup)
    }

    return optionGroups
  },

  /**
   * Returns OptionGroups in an ItemGroup ordered by sequence.
   *
   * @param itemGroupId ItemGroup id to search.
   */
  orderedOptionGroups: (_, getters) => (itemGroupId: number): OptionGroup[] => {
    return orderByOptionGroupSequence(getters.optionGroupsByItemGroupId(itemGroupId))
  }
}

export default getters
